// SSO Callback Handler Page
// PBI 760: Handle SSO callback redirection and URL cleaning

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSSO } from '../../hooks/useSSO';
import { useAuth, useLanguage } from '@/contexts';

const SSOCallback: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { handleSSOCallback } = useSSO();
  const { checkSession } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing your sign-in...');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const result = await handleSSOCallback();
        if (cancelled) return;

        if (result.success) {
          setStatus('success');
          setMessage(t('signInSuccessfully'));
          const returnUrl = localStorage.getItem('sso_return_url') || '/';
          localStorage.removeItem('sso_return_url');
          setTimeout(() => { if (!cancelled) navigate(returnUrl); }, 400);
          return;
        }

        if (result.hasValidToken && result.pendingAuth) {
          // Exponential backoff instead of fixed polling interval
          setMessage(t('signInCompleting'));
          const maxAttempts = 8; // exponential attempts (covers ~30s)
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (cancelled) return;
            const delay = Math.min(400 * Math.pow(1.6, attempt), 4000); // cap delay at 4s
            await new Promise(r => setTimeout(r, delay));
            const ok = await checkSession(true);
            if (ok) {
              if (cancelled) return;
              setStatus('success');
              setMessage(t('signInSuccessfully'));
              const returnUrl = localStorage.getItem('sso_return_url') || '/';
              localStorage.removeItem('sso_return_url');
              setTimeout(() => { if (!cancelled) navigate(returnUrl); }, 400);
              return;
            }
          }
          // Exhausted attempts
          setStatus('error');
          setMessage(t('signInFailed'));
          setTimeout(() => { if (!cancelled) navigate('/auth/login?error=session_timeout'); }, 3000);
          return;
        }

        // Hard failure path
        setStatus('error');
        setMessage(result.error || t('signInFailed'));
        setTimeout(() => { if (!cancelled) navigate('/auth/login?error=' + encodeURIComponent(result.error || 'Unknown error')); }, 3000);
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setMessage(t('unexpectedError'));
        setTimeout(() => { if (!cancelled) navigate('/auth/login?error=callback_error'); }, 3000);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [handleSSOCallback, navigate, checkSession, t]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-sm text-center">
        <div className="mb-6">
          {status === 'processing' && (
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {status === 'processing' && t('signInCompleting')}
          {status === 'success' && t('welcomeToPromptShelf')}
          {status === 'error' && t('signInFailed')}
        </h1>

        <p className="text-gray-600 mb-6">
          {message}
        </p>

        {status === 'error' && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/auth/login')}
              className="w-full bg-brand-dark-blue text-white py-2 px-4 rounded-md hover:bg-brand-electric-blue transition-colors"
            >
              t('tryAgain')
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOCallback;
